/**
 * VibeKonnectCoordinationPage — one-glance traffic-light dashboard.
 *
 * Answers the only question the super admin cares about:
 *   "Is it safe to commit + deploy from VibeKonnect right now, yes or no?"
 *
 * Big GO / WAIT / STOP verdict at the top. Four traffic-light cards below with
 * the specific next step for each. No CLI, no SSH, no jargon.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Loader2,
  GitBranch,
  Lock,
  Unlock,
  Package,
  History,
  Copy,
  ExternalLink,
  Wrench,
  Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  green: {
    bg: 'bg-emerald-50 border-emerald-300',
    accent: 'text-emerald-700',
    Icon: CheckCircle2,
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  },
  amber: {
    bg: 'bg-amber-50 border-amber-300',
    accent: 'text-amber-700',
    Icon: AlertTriangle,
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-800 border-amber-300',
  },
  red: {
    bg: 'bg-rose-50 border-rose-300',
    accent: 'text-rose-700',
    Icon: XCircle,
    dot: 'bg-rose-500',
    badge: 'bg-rose-100 text-rose-800 border-rose-300',
  },
};

const VERDICT_STYLES = {
  GO: {
    hero: 'from-emerald-500 to-green-600',
    Icon: CheckCircle2,
    subtext: 'text-emerald-50',
  },
  'PROCEED WITH CARE': {
    hero: 'from-amber-500 to-orange-500',
    Icon: AlertTriangle,
    subtext: 'text-amber-50',
  },
  STOP: {
    hero: 'from-rose-500 to-red-600',
    Icon: XCircle,
    subtext: 'text-rose-50',
  },
};

const CHECK_META = {
  lock_file: { title: 'Lock file (SYNC_LOCKS.json)', Icon: Lock },
  open_branches: { title: 'Open VibeKonnect branches', Icon: GitBranch },
  bundle_drift: { title: 'Source vs bundle version', Icon: Package },
  github_flow: { title: 'GitHub Mirror-Emergent Flow', Icon: Wrench },
  recent_edits: { title: 'Recent Emergent edits (do not overlap)', Icon: History },
};

export default function VibeKonnectCoordinationPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [rebuildJob, setRebuildJob] = useState(null); // {job_id, status, ...}

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await axios.get(`${API}/api/vibekonnect/coordination-status`);
      setData(res.data);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || 'Failed to load status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  // Poll rebuild job while running
  useEffect(() => {
    if (!rebuildJob || ['completed', 'failed'].includes(rebuildJob.status)) return;
    const t = setInterval(async () => {
      try {
        const { data: job } = await axios.get(
          `${API}/api/vibekonnect/auto-fix/rebuild-bundle/${rebuildJob.job_id}`,
        );
        setRebuildJob(job);
        if (job.status === 'completed') {
          toast.success('Bundle rebuilt — drift cleared');
          load(true);
        } else if (job.status === 'failed') {
          toast.error(`Rebuild failed: ${job.error || 'see console'}`);
        }
      } catch {
        /* keep polling */
      }
    }, 3500);
    return () => clearInterval(t);
  }, [rebuildJob, load]);

  const clearStaleLock = async () => {
    try {
      await axios.post(`${API}/api/vibekonnect/lock/clear-stale`);
      toast.success('Stale lock cleared');
      load(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to clear');
    }
  };

  const triggerAutoFix = async (fix) => {
    if (!fix?.path) return;
    try {
      const { data: res } = await axios({
        method: fix.method || 'POST',
        url: `${API}${fix.path}`,
      });
      if (fix.action_type === 'rebuild_bundle') {
        toast.success('Bundle rebuild started — this takes ~90 s');
        setRebuildJob({ job_id: res.job_id, status: res.status || 'queued' });
      } else if (fix.action_type === 'clear_stale_lock') {
        toast.success(res.message || 'Fix applied');
        load(false);
      } else {
        toast.success('Fix applied');
        load(false);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fix failed');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="vk-coord-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="vk-coord-error">
        Could not load coordination status. Check backend logs.
      </div>
    );
  }

  const verdict = data.verdict;
  const verdictStyle = VERDICT_STYLES[verdict.verdict] || VERDICT_STYLES.STOP;
  const VerdictIcon = verdictStyle.Icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vk-coordination-page">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="vk-coord-title">
              VibeKonnect Coordination
            </h1>
            <p className="text-slate-600 mt-1">
              One-glance check: is it safe to commit + deploy from VibeKonnect right now?
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                data-testid="vk-coord-auto-refresh"
              />
              Auto-refresh (30s)
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => load(false)}
              disabled={refreshing}
              data-testid="vk-coord-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* BIG VERDICT HERO */}
        <div
          className={`bg-gradient-to-br ${verdictStyle.hero} rounded-2xl p-8 text-white shadow-xl`}
          data-testid={`vk-coord-verdict-${verdict.verdict.toLowerCase().replace(/\s/g, '-')}`}
        >
          <div className="flex items-center gap-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-4">
              <VerdictIcon className="w-16 h-16" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <div className="text-sm uppercase tracking-widest opacity-80 mb-1">
                Current verdict
              </div>
              <div className="text-5xl font-black tracking-tight" data-testid="vk-coord-verdict-label">
                {verdict.emoji} {verdict.verdict}
              </div>
              <div className={`text-lg mt-2 ${verdictStyle.subtext}`}>{verdict.headline}</div>
              <div className={`text-sm mt-1 ${verdictStyle.subtext} opacity-90`}>
                {verdict.reason}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {verdict.deployment_locked ? (
                <div
                  className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2"
                  data-testid="vk-deployment-lock-badge"
                >
                  <Lock className="w-5 h-5" />
                  <span className="font-bold text-sm tracking-wide">DEPLOYMENT LOCKED</span>
                </div>
              ) : (
                <div
                  className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2"
                  data-testid="vk-deployment-unlock-badge"
                >
                  <Unlock className="w-5 h-5" />
                  <span className="font-bold text-sm tracking-wide">DEPLOY ALLOWED</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* DEPLOYMENT LOCKED — one-click fixes summary */}
        {verdict.deployment_locked && (
          <Card className="border-2 border-rose-300 bg-rose-50" data-testid="vk-fix-panel">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-rose-800 flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Fix these to unlock deployment
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {Object.entries(data.checks)
                .filter(([, c]) => c.status === 'red' && c.exact_fix)
                .map(([key, check]) => {
                  const fix = check.exact_fix;
                  const isRebuildRunning =
                    rebuildJob &&
                    fix.action_type === 'rebuild_bundle' &&
                    ['queued', 'running'].includes(rebuildJob.status);
                  return (
                    <div
                      key={key}
                      className="flex items-start justify-between gap-4 bg-white rounded-lg p-4 border border-rose-200"
                      data-testid={`vk-fix-row-${key}`}
                    >
                      <div className="flex-1">
                        <div className="text-xs uppercase tracking-wide text-rose-600 font-semibold mb-1">
                          {CHECK_META[key]?.title || key}
                        </div>
                        <div className="text-sm text-slate-800 font-medium">{check.label}</div>
                        <div className="text-xs text-slate-600 mt-1">{fix.reason || check.detail}</div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {fix.auto_fixable ? (
                          <Button
                            onClick={() => triggerAutoFix(fix)}
                            disabled={isRebuildRunning}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid={`vk-autofix-btn-${key}`}
                          >
                            {isRebuildRunning ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {rebuildJob.status === 'queued' ? 'Queued…' : 'Rebuilding…'}
                              </>
                            ) : (
                              <>
                                <Zap className="w-4 h-4 mr-2" />
                                {fix.label}
                              </>
                            )}
                          </Button>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                            Manual: {fix.label}
                          </Badge>
                        )}
                        {isRebuildRunning && (
                          <div className="text-xs text-slate-500">
                            ~{fix.expected_duration_seconds || 90} s
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              {rebuildJob && rebuildJob.status === 'completed' && (
                <div
                  className="bg-emerald-50 border border-emerald-300 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2"
                  data-testid="vk-rebuild-completed"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Bundle rebuilt successfully. Drift check now:{' '}
                  <strong>{rebuildJob.post_fix_drift_label}</strong>
                </div>
              )}
              {rebuildJob && rebuildJob.status === 'failed' && (
                <div
                  className="bg-rose-100 border border-rose-300 rounded-lg p-3 text-sm text-rose-800"
                  data-testid="vk-rebuild-failed"
                >
                  <div className="font-semibold mb-1">Rebuild failed</div>
                  <div className="text-xs">{rebuildJob.error || 'Check backend logs'}</div>
                  {rebuildJob.yarn_stderr_tail && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs">yarn stderr (tail)</summary>
                      <pre className="text-[10px] whitespace-pre-wrap mt-1 max-h-40 overflow-auto bg-white/60 p-2 rounded">
                        {rebuildJob.yarn_stderr_tail}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Playbook — what to do next based on verdict */}
        <Card className="border-2 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              {verdict.verdict === 'STOP'
                ? '🛑 Fix these before touching VibeKonnect'
                : '✅ Your next steps in VibeKonnect'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {(verdict.verdict === 'STOP'
                ? data.playbook.when_verdict_is_stop
                : data.playbook.safe_next_steps
              ).map((step, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <span>{step.replace(/^\d+\.\s*/, '')}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>

        {/* 4 traffic-light cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(data.checks).map(([key, check]) => {
            const meta = CHECK_META[key] || { title: key, Icon: CheckCircle2 };
            const style = STATUS_STYLES[check.status] || STATUS_STYLES.amber;
            const StatusIcon = style.Icon;
            const MetaIcon = meta.Icon;
            return (
              <Card
                key={key}
                className={`border-2 ${style.bg}`}
                data-testid={`vk-check-${key}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <MetaIcon className={`w-5 h-5 ${style.accent}`} />
                      <CardTitle className="text-base text-slate-800">
                        {meta.title}
                      </CardTitle>
                    </div>
                    <Badge className={`${style.badge} border font-semibold`}>
                      <StatusIcon className="w-3.5 h-3.5 mr-1" />
                      {check.status.toUpperCase()}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className={`font-semibold ${style.accent}`}>{check.label}</div>
                  <div className="text-sm text-slate-700 leading-relaxed">{check.detail}</div>

                  {/* Structured extras */}
                  {check.branches && check.branches.length > 0 && (
                    <div className="pt-2 space-y-1">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">
                        Open branches
                      </div>
                      {check.branches.map((b) => (
                        <div
                          key={b}
                          className="flex items-center justify-between bg-white/70 rounded px-2 py-1 text-xs font-mono"
                        >
                          <span>{b}</span>
                          <button
                            onClick={() => copy(b)}
                            className="text-slate-400 hover:text-slate-700"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {check.locked_files && check.locked_files.length > 0 && (
                    <div className="pt-2 space-y-1">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">
                        Locked files
                      </div>
                      {check.locked_files.map((f) => (
                        <div
                          key={f}
                          className="bg-white/70 rounded px-2 py-1 text-xs font-mono text-slate-700"
                        >
                          {f}
                        </div>
                      ))}
                    </div>
                  )}

                  {check.tag_counts && (
                    <div className="pt-2 space-y-2">
                      <div className="text-xs text-slate-500 uppercase tracking-wide">
                        Release-history tag distribution (last 40)
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-white/70 rounded px-2 py-1 text-center">
                          <div className="text-slate-500">[emergent]</div>
                          <div className="font-mono font-bold text-emerald-700">
                            {check.tag_counts['[emergent]'] || 0}
                          </div>
                        </div>
                        <div className="bg-white/70 rounded px-2 py-1 text-center">
                          <div className="text-slate-500">[external]</div>
                          <div className="font-mono font-bold text-slate-700">
                            {check.tag_counts['[external]'] || 0}
                          </div>
                        </div>
                        <div className="bg-white/70 rounded px-2 py-1 text-center">
                          <div className="text-slate-500">[vibekonnect]</div>
                          <div className="font-mono font-bold text-purple-700">
                            {check.tag_counts['[vibekonnect]'] || 0}
                          </div>
                        </div>
                      </div>
                      {check.branch_protection_setup_url && (
                        <a
                          href={check.branch_protection_setup_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline"
                          data-testid="vk-branch-protection-link"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Enable branch protection on `main` (opens GitHub Settings)
                        </a>
                      )}
                    </div>
                  )}

                  {check.recent_files && check.recent_files.length > 0 && (
                    <details className="pt-2">
                      <summary className="text-xs text-slate-500 uppercase tracking-wide cursor-pointer hover:text-slate-700">
                        Recent files touched ({check.recent_files.length}) — click to expand
                      </summary>
                      <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                        {check.recent_files.map((f) => (
                          <div
                            key={f}
                            className="bg-white/70 rounded px-2 py-1 text-xs font-mono text-slate-700"
                          >
                            {f}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {check.source_version && check.bundle_file && (
                    <div className="pt-2 grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white/70 rounded px-2 py-1">
                        <div className="text-slate-500">Source</div>
                        <div className="font-mono font-semibold">v{check.source_version}</div>
                      </div>
                      <div className="bg-white/70 rounded px-2 py-1">
                        <div className="text-slate-500">Bundle</div>
                        <div className="font-mono font-semibold truncate">{check.bundle_file}</div>
                      </div>
                    </div>
                  )}

                  {/* Next-step callout */}
                  <div className="pt-2 mt-2 border-t border-white/60">
                    <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                      Next step
                    </div>
                    <div className="text-sm font-medium text-slate-800">{check.next_step}</div>
                  </div>

                  {/* Contextual action button */}
                  {key === 'lock_file' && check.status === 'green' && check.label.includes('Stale') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={clearStaleLock}
                      className="mt-2"
                      data-testid="vk-clear-stale-lock-btn"
                    >
                      Clear stale lock file
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Reserved paths ledger */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-base">Reserved paths (never touch each other&apos;s territory)</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-purple-500"></span>
                VibeKonnect owns (Emergent will not touch)
              </div>
              <ul className="space-y-1">
                {data.reserved_paths.vibekonnect_owns.map((p) => (
                  <li key={p} className="font-mono text-xs bg-purple-50 rounded px-2 py-1 text-purple-900">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                Emergent owns (VibeKonnect will not touch)
              </div>
              <ul className="space-y-1">
                {data.reserved_paths.emergent_owns.map((p) => (
                  <li key={p} className="font-mono text-xs bg-emerald-50 rounded px-2 py-1 text-emerald-900">
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Timestamp + protocol link */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-4">
          <div>
            Last checked: {new Date(data.generated_at).toLocaleString()}
          </div>
          <a
            href="/app/memory/PROTOCOL_WITH_VIBEKONNECT.md"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 hover:text-slate-800"
          >
            Read the coordination protocol
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
}
